import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { onBillingUsageChanged, onPlanChanged } from '@/lib/billingEvents';
import {
  canProcessPendingApplications,
  reconcileDeferredCountsWithApplications,
} from '@/lib/pendingProcessing';
import { patchUnlockedDeferredApplications } from '@/lib/recruiterCache';
import { isApplicationsHydrateComplete } from '@/lib/applicationListSync';
import { usePendingBillingStore } from '@/stores/pendingBillingStore';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useApplicationsQuery } from '@/hooks/useRecruiterListQueries';

/**
 * Syncs persisted deferred-application counts from the BE on every page.
 * Calls unlock-deferred first so PENDING_UPGRADE → READY_TO_PROCESS is
 * written to Firestore when the plan has capacity.
 */
export function useSyncPendingBillingCount() {
  const { selectedCompany } = useAuth();
  const queryClient = useQueryClient();
  const setDeferredCounts = usePendingBillingStore(s => s.setDeferredCounts);
  const { data: billingStatus } = useBillingStatus();
  const hasProcessingCredits = canProcessPendingApplications(billingStatus);
  const companyId = selectedCompany?.id;
  const applicationsQuery = useApplicationsQuery(companyId, false);

  const applyCounts = useCallback(
    (
      api: { pendingUpgrade: number; readyToProcess: number; billingRetry: number },
      options?: { mergeFromList?: boolean },
    ) => {
      if (!companyId) return;
      const mergeFromList = options?.mergeFromList
        ?? isApplicationsHydrateComplete(companyId, false);
      const merged = reconcileDeferredCountsWithApplications(
        queryClient,
        companyId,
        hasProcessingCredits,
        api,
        { mergeFromList },
      );
      setDeferredCounts(merged);
    },
    [companyId, queryClient, hasProcessingCredits, setDeferredCounts],
  );

  const sync = useCallback(async () => {
    if (!companyId) {
      setDeferredCounts({ pendingUpgrade: 0, readyToProcess: 0, billingRetry: 0 });
      return;
    }
    try {
      const result = await api.applications.unlockDeferred(companyId);
      if (result.unlocked > 0) {
        patchUnlockedDeferredApplications(queryClient, companyId);
      }
      applyCounts({
        pendingUpgrade: result.pendingUpgrade,
        readyToProcess: result.readyToProcess,
        billingRetry: result.billingRetry,
      }, { mergeFromList: false });
    } catch {
      // Keep the last known counts on transient fetch errors.
    }
  }, [companyId, setDeferredCounts, queryClient, applyCounts]);

  const syncRef = useRef(sync);
  syncRef.current = sync;

  useEffect(() => {
    void syncRef.current();
  }, [companyId]);

  useEffect(() => {
    return onPlanChanged(() => {
      void syncRef.current();
    });
  }, [companyId]);

  useEffect(() => {
    return onBillingUsageChanged(() => {
      void syncRef.current();
    });
  }, [companyId]);

  // After the full applications hydrate, merge list-derived counts once.
  const wasHydrateCompleteRef = useRef(false);
  useEffect(() => {
    const hydrateComplete = companyId
      ? isApplicationsHydrateComplete(companyId, false)
      : false;
    const justCompleted = hydrateComplete && !wasHydrateCompleteRef.current;
    wasHydrateCompleteRef.current = hydrateComplete;

    if (!companyId || !justCompleted || !applicationsQuery.data?.length) return;
    const current = usePendingBillingStore.getState();
    applyCounts({
      pendingUpgrade: current.pendingUpgrade,
      readyToProcess: current.readyToProcess,
      billingRetry: current.billingRetry,
    }, { mergeFromList: true });
  }, [companyId, applicationsQuery.data?.length, applicationsQuery.dataUpdatedAt, applyCounts]);
}
